import { canonicalDigest, canonicalJson, contractError, validateSha256 } from '@djgg/gameplay-kit';

export const FAILED_CANDIDATE_SCHEMA_VERSION = 1 as const;
export const PREVIEW_SAFETY_RECIPE_REVISION = 'candidate-preview-safety@1' as const;
export const TRUSTED_LOCAL_PREVIEW_CAPABILITY = 'trusted-local-preview@1' as const;

export type PreviewEligibilityStatus =
  | 'eligible'
  | 'safety-rejected'
  | 'safety-unavailable'
  | 'runtime-unsupported'
  | 'no-runnable-output';

export interface ImmutableCandidateObjectV1 {
  /** Private object only. A mutable URL is never an artifact identity. */
  path: string;
  sha256: string;
  generation: string;
  bytes: number;
  contentType: string;
}

export interface CandidateInventoryEntryV1 extends ImmutableCandidateObjectV1 {
  /** POSIX path relative to the candidate dist root. */
  relativePath: string;
}

export interface FailedCandidateV1 {
  schemaVersion: 1;
  candidateId: string;
  creationId: string;
  version: number;
  /** The claim which was current when the create-only artifact write committed. */
  claimId: string;
  failure: {
    stage: string;
    code: string;
    originalVerdictDigest: string;
  };
  source?: ImmutableCandidateObjectV1;
  dist?: {
    digest: string;
    /** Generation of the immutable inventory manifest itself. */
    generation: string;
    inventory: CandidateInventoryEntryV1[];
  };
  media: ImmutableCandidateObjectV1[];
  missingAssetNames: string[];
  seed: number;
  contractDigest: string;
  mediaBasisDigest: string;
  recipeDigest: string;
  gateVerdictDigest: string;
  evidenceDigests: string[];
  inputTapeDigest?: string;
  /** Private create-only attachments; never treated as preview assets. */
  evidenceObjects?: ImmutableCandidateObjectV1[];
  inputTape?: ImmutableCandidateObjectV1;
  shellCapability?: typeof TRUSTED_LOCAL_PREVIEW_CAPABILITY;
  recheckRuntime: {
    builderImage: string;
    repositorySha: string;
    nodeVersion: string;
    toolchainDigest: string;
    dependencyCatalogDigest: string;
    harnessRevision: string;
    recheckRecipeRevision: string;
    providerRevisionsDigest?: string;
  };
  recordedAt: number;
}

export interface CandidateSafetyAssessmentV1 {
  recipeRevision: typeof PREVIEW_SAFETY_RECIPE_REVISION;
  candidateId: string;
  distDigest?: string;
  assessedGeneration?: string;
  secretScan: { verdict: 'pass' | 'fail' | 'unavailable'; digest?: string };
  codeSafety: { verdict: 'pass' | 'fail' | 'unavailable'; digest?: string };
  prohibitedEndpoints: string[];
}

export interface PreviewEligibilityV1 {
  schemaVersion: 1;
  candidateId: string;
  status: PreviewEligibilityStatus;
  safetyRecipeRevision: typeof PREVIEW_SAFETY_RECIPE_REVISION;
  artifactDigest?: string;
  artifactGeneration?: string;
  secretScanDigest?: string;
  codeSafetyDigest?: string;
  reasonCodes: string[];
  assessedAt: number;
}

const ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const PREVIEW_CONTENT_TYPES = new Set([
  'text/html',
  'text/css',
  'text/javascript',
  'application/javascript',
  'application/json',
  'application/wasm',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/svg+xml',
  'audio/mpeg',
  'audio/ogg',
  'audio/wav',
  'font/woff2',
]);
const DIAGNOSTIC_CONTENT_TYPES = new Set([
  ...PREVIEW_CONTENT_TYPES,
  'application/gzip', 'application/x-gzip', 'application/x-tar',
  'application/zip', 'application/octet-stream', 'text/plain',
]);

function text(value: unknown, path: string, max = 1_024): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > max) {
    contractError(path, `expected nonempty text up to ${max} characters`);
  }
  return value;
}

function stableId(value: unknown, path: string): string {
  const parsed = text(value, path, 128);
  if (!ID.test(parsed)) contractError(path, 'expected a stable identifier');
  return parsed;
}

export function isSafeCandidateRelativePath(value: string): boolean {
  if (!value || value.startsWith('/') || value.includes('\\') || value.includes('\0')) return false;
  if (value.includes('?') || value.includes('#') || /%(?:2e|2f|5c)/i.test(value)) return false;
  const parts = value.split('/');
  return parts.every((part) => part.length > 0 && part !== '.' && part !== '..');
}

function validateObjectRef(value: ImmutableCandidateObjectV1, path: string, previewServable = false): ImmutableCandidateObjectV1 {
  const objectPath = text(value.path, `${path}.path`);
  if (!objectPath.startsWith('gs://')) contractError(`${path}.path`, 'expected a private gs:// object reference');
  const generation = text(value.generation, `${path}.generation`, 128);
  if (!/^\d+$/.test(generation)) contractError(`${path}.generation`, 'expected immutable numeric object generation');
  if (!Number.isSafeInteger(value.bytes) || value.bytes < 0) contractError(`${path}.bytes`, 'expected nonnegative integer');
  const contentType = text(value.contentType, `${path}.contentType`, 128).toLowerCase();
  if (!(previewServable ? PREVIEW_CONTENT_TYPES : DIAGNOSTIC_CONTENT_TYPES).has(contentType)) {
    contractError(`${path}.contentType`, previewServable ? 'content type is not preview-allowlisted' : 'content type is not diagnostic-allowlisted');
  }
  return {
    path: objectPath,
    sha256: validateSha256(value.sha256, `${path}.sha256`),
    generation,
    bytes: value.bytes,
    contentType,
  };
}

export function validateFailedCandidateV1(value: FailedCandidateV1): FailedCandidateV1 {
  if (!value || typeof value !== 'object') contractError('candidate', 'expected object');
  if (value.schemaVersion !== FAILED_CANDIDATE_SCHEMA_VERSION) contractError('candidate.schemaVersion', 'unsupported version');
  if (!value.failure || !Array.isArray(value.media) || !Array.isArray(value.missingAssetNames)
    || !Array.isArray(value.evidenceDigests) || !value.recheckRuntime) {
    contractError('candidate', 'missing required evidence inventory');
  }
  const inventory = value.dist?.inventory.map((entry, index) => {
    const relativePath = text(entry.relativePath, `candidate.dist.inventory[${index}].relativePath`);
    if (!isSafeCandidateRelativePath(relativePath)) contractError(`candidate.dist.inventory[${index}].relativePath`, 'unsafe relative path');
    return { ...validateObjectRef(entry, `candidate.dist.inventory[${index}]`, true), relativePath };
  });
  if (inventory && new Set(inventory.map((entry) => entry.relativePath)).size !== inventory.length) {
    contractError('candidate.dist.inventory', 'duplicate relative path');
  }
  const candidate: FailedCandidateV1 = {
    ...structuredClone(value),
    candidateId: stableId(value.candidateId, 'candidate.candidateId'),
    creationId: stableId(value.creationId, 'candidate.creationId'),
    claimId: stableId(value.claimId, 'candidate.claimId'),
    version: Number(value.version),
    failure: {
      stage: text(value.failure.stage, 'candidate.failure.stage', 128),
      code: text(value.failure.code, 'candidate.failure.code', 128),
      originalVerdictDigest: validateSha256(value.failure.originalVerdictDigest, 'candidate.failure.originalVerdictDigest'),
    },
    ...(value.source ? { source: validateObjectRef(value.source, 'candidate.source') } : {}),
    ...(value.dist ? {
      dist: {
        digest: validateSha256(value.dist.digest, 'candidate.dist.digest'),
        generation: text(value.dist.generation, 'candidate.dist.generation', 128),
        inventory: inventory!,
      },
    } : {}),
    media: value.media.map((entry, index) => validateObjectRef(entry, `candidate.media[${index}]`)),
    missingAssetNames: value.missingAssetNames.map((name, index) => text(name, `candidate.missingAssetNames[${index}]`, 128)),
    seed: Number(value.seed),
    contractDigest: validateSha256(value.contractDigest, 'candidate.contractDigest'),
    mediaBasisDigest: validateSha256(value.mediaBasisDigest, 'candidate.mediaBasisDigest'),
    recipeDigest: validateSha256(value.recipeDigest, 'candidate.recipeDigest'),
    gateVerdictDigest: validateSha256(value.gateVerdictDigest, 'candidate.gateVerdictDigest'),
    evidenceDigests: value.evidenceDigests.map((digest, index) => validateSha256(digest, `candidate.evidenceDigests[${index}]`)),
    ...(value.inputTapeDigest ? { inputTapeDigest: validateSha256(value.inputTapeDigest, 'candidate.inputTapeDigest') } : {}),
    ...(value.evidenceObjects ? { evidenceObjects: value.evidenceObjects.map((entry, index) => validateObjectRef(entry, `candidate.evidenceObjects[${index}]`)) } : {}),
    ...(value.inputTape ? { inputTape: validateObjectRef(value.inputTape, 'candidate.inputTape') } : {}),
  };
  if (!Number.isSafeInteger(candidate.version) || candidate.version < 1) contractError('candidate.version', 'expected positive integer');
  if (!Number.isSafeInteger(candidate.seed) || candidate.seed < 0 || candidate.seed > 0xffffffff) contractError('candidate.seed', 'expected uint32');
  if (!Number.isSafeInteger(candidate.recordedAt) || candidate.recordedAt <= 0) contractError('candidate.recordedAt', 'expected timestamp');
  if (value.source === undefined) delete candidate.source;
  if (value.dist === undefined) delete candidate.dist;
  if (value.inputTapeDigest === undefined) delete candidate.inputTapeDigest;
  if (value.evidenceObjects === undefined) delete candidate.evidenceObjects;
  if (value.inputTape === undefined) delete candidate.inputTape;
  if (candidate.inputTape && candidate.inputTapeDigest && candidate.inputTape.sha256 !== candidate.inputTapeDigest) {
    contractError('candidate.inputTape', 'object digest does not match inputTapeDigest');
  }
  if (value.shellCapability === undefined) delete candidate.shellCapability;
  else if (value.shellCapability !== TRUSTED_LOCAL_PREVIEW_CAPABILITY) contractError('candidate.shellCapability', 'unsupported preview capability');
  if (candidate.dist && !/^\d+$/.test(candidate.dist.generation)) contractError('candidate.dist.generation', 'expected immutable numeric object generation');
  if (!/^[a-f0-9]{40}(?:[a-f0-9]{24})?$/.test(candidate.recheckRuntime.repositorySha)) {
    contractError('candidate.recheckRuntime.repositorySha', 'expected git sha');
  }
  validateSha256(candidate.recheckRuntime.toolchainDigest, 'candidate.recheckRuntime.toolchainDigest');
  validateSha256(candidate.recheckRuntime.dependencyCatalogDigest, 'candidate.recheckRuntime.dependencyCatalogDigest');
  text(candidate.recheckRuntime.builderImage, 'candidate.recheckRuntime.builderImage', 512);
  text(candidate.recheckRuntime.nodeVersion, 'candidate.recheckRuntime.nodeVersion', 64);
  text(candidate.recheckRuntime.harnessRevision, 'candidate.recheckRuntime.harnessRevision', 128);
  text(candidate.recheckRuntime.recheckRecipeRevision, 'candidate.recheckRuntime.recheckRecipeRevision', 128);
  if (candidate.recheckRuntime.providerRevisionsDigest !== undefined) {
    validateSha256(candidate.recheckRuntime.providerRevisionsDigest, 'candidate.recheckRuntime.providerRevisionsDigest');
  }
  return candidate;
}

export async function failedCandidateIdentity(value: FailedCandidateV1): Promise<string> {
  return canonicalDigest(validateFailedCandidateV1(value));
}

/**
 * Claim-fenced, create-only attachment semantics. A retry may observe the exact
 * same record, but can never replace immutable failed evidence.
 */
export function attachFailedCandidate(
  existing: FailedCandidateV1 | null,
  proposed: FailedCandidateV1,
  currentClaimId: string | null,
  terminalStatus: 'failed' | 'cancelled',
): { candidate: FailedCandidateV1; created: boolean } {
  const candidate = validateFailedCandidateV1(proposed);
  if (existing) {
    const retained = validateFailedCandidateV1(existing);
    if (canonicalJson(retained) !== canonicalJson(candidate)) contractError('candidate', 'immutable candidate replacement refused');
    return { candidate: retained, created: false };
  }
  if (terminalStatus !== 'failed') contractError('candidate', 'cancelled work cannot attach a candidate');
  if (!currentClaimId || currentClaimId !== candidate.claimId) contractError('candidate.claimId', 'claim ownership lost');
  return { candidate, created: true };
}

function eligibility(
  candidate: FailedCandidateV1,
  status: PreviewEligibilityStatus,
  reasonCodes: string[],
  assessedAt: number,
  assessment?: CandidateSafetyAssessmentV1,
): PreviewEligibilityV1 {
  return {
    schemaVersion: 1,
    candidateId: candidate.candidateId,
    status,
    safetyRecipeRevision: PREVIEW_SAFETY_RECIPE_REVISION,
    ...(candidate.dist ? { artifactDigest: candidate.dist.digest } : {}),
    ...(assessment?.assessedGeneration ? { artifactGeneration: assessment.assessedGeneration } : {}),
    ...(assessment?.secretScan.digest ? { secretScanDigest: assessment.secretScan.digest } : {}),
    ...(assessment?.codeSafety.digest ? { codeSafetyDigest: assessment.codeSafety.digest } : {}),
    reasonCodes,
    assessedAt,
  };
}

/** Server-owned safety decision. Retention by itself never reaches `eligible`. */
export function evaluatePreviewEligibility(
  input: FailedCandidateV1,
  assessment: CandidateSafetyAssessmentV1 | null,
  assessedAt: number,
): PreviewEligibilityV1 {
  const candidate = validateFailedCandidateV1(input);
  if (!candidate.dist || candidate.dist.inventory.length === 0) {
    return eligibility(candidate, 'no-runnable-output', ['dist-missing'], assessedAt);
  }
  if (candidate.shellCapability !== TRUSTED_LOCAL_PREVIEW_CAPABILITY) {
    return eligibility(candidate, 'runtime-unsupported', ['trusted-local-preview-capability-missing'], assessedAt);
  }
  if (!assessment || assessment.secretScan.verdict === 'unavailable' || assessment.codeSafety.verdict === 'unavailable') {
    return eligibility(candidate, 'safety-unavailable', ['mandatory-safety-verdict-unavailable'], assessedAt, assessment ?? undefined);
  }
  if (assessment.candidateId !== candidate.candidateId || assessment.distDigest !== candidate.dist.digest) {
    return eligibility(candidate, 'safety-rejected', ['artifact-identity-mismatch'], assessedAt, assessment);
  }
  if (!assessment.assessedGeneration || assessment.assessedGeneration !== candidate.dist.generation) {
    return eligibility(candidate, 'safety-rejected', ['artifact-generation-mismatch'], assessedAt, assessment);
  }
  const rejected: string[] = [];
  if (assessment.recipeRevision !== PREVIEW_SAFETY_RECIPE_REVISION) rejected.push('safety-recipe-mismatch');
  if (assessment.secretScan.verdict !== 'pass' || !assessment.secretScan.digest) rejected.push('secret-scan-failed');
  if (assessment.codeSafety.verdict !== 'pass' || !assessment.codeSafety.digest) rejected.push('code-safety-failed');
  if (assessment.prohibitedEndpoints.length > 0) rejected.push('prohibited-endpoint');
  if (rejected.length > 0) return eligibility(candidate, 'safety-rejected', rejected, assessedAt, assessment);
  return eligibility(candidate, 'eligible', [], assessedAt, assessment);
}
